import React, { useState, useEffect } from 'react';
import { Button } from 'antd';
import { XCircle, AlertTriangle, AlertCircle, Download } from 'lucide-react';
import './BrowserNotAllowedOverlay.scss';

interface BrowserNotAllowedOverlayProps {
  visible: boolean;
  browserType: 'firefox' | 'safari' | 'other';
  onDismiss?: () => void;
}

interface BrowserConfig {
  title: string;
  icon: React.ReactNode;
  severity: 'error' | 'warning';
  message: string;
  reason: string;
  suggestion: string;
  dismissable: boolean;
  ctaText: string;
  ctaLink: string;
}

export const BrowserNotAllowedOverlay: React.FC<BrowserNotAllowedOverlayProps> = ({
  visible,
  browserType,
  onDismiss
}) => {
  const browserMessages: Record<string, BrowserConfig> = {
    firefox: {
      title: 'Firefox Not Supported',
      icon: <XCircle size={64} className="error-icon" />,
      severity: 'error',
      message: 'Dadan Recording Studio requires a Chromium-based browser for optimal recording quality.',
      reason: 'Firefox uses different media encoding APIs that are not compatible with our recording technology.',
      suggestion: 'Please use Chrome, Edge, or Brave to continue.',
      dismissable: false,
      ctaText: 'Download Chrome',
      ctaLink: 'https://www.google.com/chrome/'
    },
    safari: {
      title: 'Safari Has Limitations',
      icon: <AlertTriangle size={64} className="warning-icon" />,
      severity: 'warning',
      message: 'Safari may experience recording issues due to limited codec support.',
      reason: 'Some features like 2k recording and noise suppression may not work correctly.',
      suggestion: 'For best results, use Chrome, Edge, or Brave. You can continue, but quality may be affected.',
      dismissable: true,
      ctaText: 'Download Chrome',
      ctaLink: 'https://www.google.com/chrome/'
    },
    other: {
      title: 'Unsupported Browser',
      icon: <AlertCircle size={64} className="warning-icon" />,
      severity: 'warning',
      message: 'Your browser may not support all recording features.',
      reason: 'We recommend using a modern Chromium-based browser for full compatibility.',
      suggestion: 'Please use Chrome, Edge, or Brave for the best experience.',
      dismissable: false,
      ctaText: 'Download Chrome',
      ctaLink: 'https://www.google.com/chrome/'
    }
  };

  const config = browserMessages[browserType];
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (browserType === 'safari') {
      const dismissedDate = localStorage.getItem('safari-warning-dismissed');
      if (dismissedDate) {
        const daysSinceDismiss = (Date.now() - parseInt(dismissedDate)) / (1000 * 60 * 60 * 24);
        if (daysSinceDismiss < 7) {
          setDismissed(true);
        }
      }
    }
  }, [browserType]);

  const handleDismiss = () => {
    if (config.dismissable) {
      localStorage.setItem('safari-warning-dismissed', Date.now().toString());
      setDismissed(true);
      onDismiss?.();
      console.log('[BrowserOverlay] Safari warning dismissed, will show again in 7 days');
    }
  };

  if (!visible || dismissed) return null;

  return (
    <div className="browser-not-allowed-overlay">
      <div className="overlay-backdrop" />
      <div className="overlay-content">
        <div className={`icon-wrapper ${config.severity}`}>
          {config.icon}
        </div>
        
        <h2 className="title">{config.title}</h2>
        <p className="message">{config.message}</p>
        <p className="reason">{config.reason}</p>
        <p className="suggestion">{config.suggestion}</p>

        <div className="actions">
          <Button
            type="primary"
            size="large"
            href={config.ctaLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Download size={18} />
            {config.ctaText}
          </Button>

          {config.dismissable && (
            <Button
              type="text"
              size="large"
              onClick={handleDismiss}
            >
              Continue Anyway
            </Button>
          )}
        </div>

        <div className="supported-browsers">
          <p className="label">Supported Browsers:</p>
          <div className="browser-list">
            <span>✓ Google Chrome</span>
            <span>✓ Microsoft Edge</span>
            <span>✓ Brave Browser</span>
          </div>
        </div>
      </div>
    </div>
  );
};
